import { Button, Card, CardContent, CardHeader, Input, ScrollArea } from '@ibsys/kit';
import { FC, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SaveIcon } from 'lucide-react';
import {
  useExpertReportWithVersion,
  useDeleteExpertReport,
  useUpdateExpertReport,
  flattenBlocks,
} from '@/entities/expert-report';
import { ExpertReportVersionDropdown } from '@/features/expert-report/ExpertReportVersionDropdown/ExpertReportVersionDropdown';
import { PredicateInput } from '@/features/PredicateInput';
import { useHasPermission } from '@/entities/profile';
import { ConfirmationButton } from '@/shared/ui';

type ExpertReportCardProps = {
  reportId: number;
  onDeleted: () => void;
  className?: string;
};

export const ExpertReportCard: FC<ExpertReportCardProps> = ({ reportId, onDeleted, className }) => {
  const { t } = useTranslation(['common', 'expertReport']);
  const {
    selectedReport: report,
    baseReport,
    isLoading,
    isLastVersion,
    versionId,
    setVersion,
  } = useExpertReportWithVersion(reportId);
  const { mutateAsync: deleteReport, isPending: isDeleting } = useDeleteExpertReport();
  const { mutateAsync: updateReport, isPending: isUpdating } = useUpdateExpertReport();
  const allowAdmin = useHasPermission('ADMIN_EXPERT_REPORT');
  // Редактирование доступно только у последней версии и при праве администрирования
  const canEdit = isLastVersion && allowAdmin;
  const [name, setName] = useState('');
  const [predicate, setPredicate] = useState<{ dsl: string; dslHuman: string }>({ dsl: '', dslHuman: '' });
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (report) {
      setName(report.name);
      setPredicate({
        dsl: report.predicate?.dsl ?? '',
        dslHuman: report.predicate?.dslHuman ?? '',
      });
      setHasChanges(false);
    }
  }, [report]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    setHasChanges(e.target.value !== report?.name);
  };

  const handlePredicateChange = useCallback((value: { dsl: string; dslHuman: string }) => {
    setPredicate(value);
    setHasChanges(true);
  }, []);

  const handleSave = async () => {
    if (!baseReport || !name.trim()) return;
    const predicateInput = predicate.dsl ? { dsl: predicate.dsl, dslHuman: predicate.dslHuman } : undefined;
    await updateReport({
      id: reportId,
      body: {
        name: name.trim(),
        predicate: predicateInput,
        blocks: flattenBlocks(baseReport.blocks),
      },
    });
    setHasChanges(false);
  };

  const handleDelete = async () => {
    await deleteReport(reportId);
    onDeleted();
  };

  if (isLoading) {
    return (
      <Card className="w-full h-fit">
        <CardHeader>
          <div className="text-muted-foreground">{t('common:loading')}</div>
        </CardHeader>
      </Card>
    );
  }

  if (!report) {
    return (
      <Card className="w-full h-fit">
        <CardHeader>
          <div className="text-muted-foreground">{t('expertReport:selectItem')}</div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="space-y-4">
          <ExpertReportVersionDropdown reportId={reportId} value={versionId} onChange={setVersion} />

          <Input
            id="report-name"
            label={t('expertReport:reportName')}
            value={name}
            onChange={handleNameChange}
            disabled={!canEdit}
          />

          <PredicateInput
            label={t('expertReport:predicate')}
            type="textarea"
            value={predicate}
            onChange={handlePredicateChange}
            disabled={!canEdit}
          />
        </div>
      </CardHeader>
      <CardContent className={'min-h-0'}>
        <ScrollArea>
          <div className="flex items-center gap-2">
            {canEdit && hasChanges && (
              <Button
                variant="default"
                onClick={handleSave}
                disabled={isUpdating || !name.trim()}
                icon={<SaveIcon className="size-4" />}
              >
                {t('common:save')}
              </Button>
            )}

            {canEdit && (
              <ConfirmationButton
                onConfirm={handleDelete}
                isLoadingConfirm={isDeleting}
                title={t('expertReport:removeReport')}
                description={
                  <Trans
                    i18nKey="expertReport:removeReportDescription"
                    values={{ reportName: report.name }}
                    components={{ strong: <strong /> }}
                  />
                }
                trigger={{
                  variant: 'outline',
                  color: 'destructive',
                  children: t('expertReport:deleteReport'),
                }}
              />
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};
